import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, CheckCircle, Clock, Play, UserPlus, Calendar, Wrench } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import type { ProfessionalDashboardStats } from '../../../shared/professional-types';
import CreatePatientModal from '../components/professional/CreatePatientModal';

export default function ProfessionalDashboardPage() {
  const { user, profile } = useAuthStore();
  const [stats, setStats] = useState<ProfessionalDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/professional/dashboard/stats');
      if (response.data.ok) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Pacientes Activos',
      value: stats?.pacientes_activos || 0,
      icon: Users,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: 'Sesiones Completadas',
      value: stats?.sesiones_completadas || 0,
      icon: CheckCircle,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
    },
    {
      title: 'Sesiones Pendientes',
      value: stats?.sesiones_pendientes || 0,
      icon: Clock,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10',
    },
    {
      title: 'Sesiones en Curso',
      value: stats?.sesiones_en_curso || 0,
      icon: Play,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
    },
  ];

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-display font-bold mb-2">
            Panel Profesional
          </h1>
          <p className="text-white/70 text-lg">
            Bienvenido, {profile?.first_name || user?.username}
          </p>
        </motion.div>

        {/* Stats Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-neureka-yellow"></div>
            <p className="mt-4 text-white/70">Cargando estadísticas...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {statCards.map((card, index) => (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-container group hover:scale-105 transition-transform cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-lg ${card.bgColor}`}>
                      <card.icon className={`w-6 h-6 ${card.color}`} />
                    </div>
                    <div className="text-3xl font-bold">
                      {card.value}
                    </div>
                  </div>
                  <h3 className="text-white/70 text-sm font-medium">
                    {card.title}
                  </h3>
                </motion.div>
              ))}
            </div>

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-container"
            >
              <h2 className="text-2xl font-display font-bold mb-6">
                Acciones Rápidas
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link
                  to="/profesional/pacientes"
                  className="flex items-center gap-4 p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group"
                >
                  <div className="p-3 rounded-lg bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Gestionar Pacientes</h3>
                    <p className="text-sm text-white/60">
                      Ver lista completa de pacientes
                    </p>
                  </div>
                </Link>

                <Link
                  to="/profesional/sesiones"
                  className="flex items-center gap-4 p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group"
                >
                  <div className="p-3 rounded-lg bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                    <Calendar className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Gestionar Sesiones</h3>
                    <p className="text-sm text-white/60">
                      Ver y crear sesiones de trabajo
                    </p>
                  </div>
                </Link>

                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-4 p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group text-left"
                >
                  <div className="p-3 rounded-lg bg-green-500/10 group-hover:bg-green-500/20 transition-colors">
                    <UserPlus className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Crear Paciente</h3>
                    <p className="text-sm text-white/60">
                      Dar de alta un nuevo paciente
                    </p>
                  </div>
                </button>

                <Link
                  to="/herramientas"
                  className="flex items-center gap-4 p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group"
                >
                  <div className="p-3 rounded-lg bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
                    <Wrench className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Herramientas</h3>
                    <p className="text-sm text-white/60">
                      Ejercicios de logopedia y rehabilitación
                    </p>
                  </div>
                </Link>
              </div>
            </motion.div>
          </>
        )}

        {/* Modal de crear paciente */}
        <CreatePatientModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onPatientCreated={() => {
            setShowCreateModal(false);
            loadDashboardStats();
          }}
        />
      </div>
    </div>
  );
}
